import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { utils, writeFile } from "xlsx";
import ExcelJS from "exceljs";
import moment from "moment-timezone";
import { useAuthUser } from "../../../../hooks/useAuthUser";
import RetreatApi from "../../../../api/retreatApi";
import AuthApi from "../../../../api/authApi";
import WaitListCoupleApi from "../../../../api/waitListCoupleApi";
import Helpers from "../../../../utils/helpers";
import { sortParishesByName } from "../../../../utils/parishLabels";
import type { LifecycleRetreat } from "../../../../types/retreatLifecycle";
import { formatLanguage } from "../lifecycle/lifecycleUi";
import { registrationOpenColumns } from "../lifecycle/manageRetreatColumns";
import {
  SECTION_IDS,
  emptySearch,
  type SectionKey,
} from "./manageRetreatTypes";

export function useManageRetreat() {
  const location = useLocation();
  const userData = useAuthUser();
  const isSuperAdmin = userData?.userRole === "ADMIN";
  const canEdit =
    isSuperAdmin ||
    userData?.committeeMember === true ||
    userData?.access === true;
  const retreatApi = useMemo(() => new RetreatApi(), []);
  const authApi = useMemo(() => new AuthApi(), []);
  const waitListApi = useMemo(() => new WaitListCoupleApi(), []);

  const [counts, setCounts] = useState({
    projected: 0,
    inSetup: 0,
    registrationOpen: 0,
    closing: 0,
    completed: 0,
    deleted: 0,
  });
  const [parishes, setParishes] = useState<any[]>([]);
  const [parishFilter, setParishFilter] = useState("");
  const [projectedParishFilter, setProjectedParishFilter] = useState("");
  const [projectedTypeFilter, setProjectedTypeFilter] = useState("");
  const [showPasswordMailModal, setShowPasswordMailModal] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const [accessedRetreats, setAccessedRetreats] = useState<any[]>([]);
  const [restoreTarget, setRestoreTarget] = useState<LifecycleRetreat | null>(
    null,
  );
  const [historyRetreat, setHistoryRetreat] = useState<LifecycleRetreat | null>(
    null,
  );
  const [historyEntries, setHistoryEntries] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [rows, setRows] = useState<Record<SectionKey, LifecycleRetreat[]>>({
    projected: [],
    inSetup: [],
    registrationOpen: [],
    closing: [],
    completed: [],
    deleted: [],
  });
  const [totals, setTotals] = useState<Record<SectionKey, number>>({
    projected: 0,
    inSetup: 0,
    registrationOpen: 0,
    closing: 0,
    completed: 0,
    deleted: 0,
  });
  const [search, setSearch] = useState<Record<SectionKey, string>>(emptySearch);
  const [loadingSection, setLoadingSection] = useState<
    Record<SectionKey, boolean>
  >({
    projected: true,
    inSetup: true,
    registrationOpen: true,
    closing: true,
    completed: true,
    deleted: true,
  });
  const debounceRef = useRef<
    Record<SectionKey, ReturnType<typeof setTimeout> | null>
  >({
    projected: null,
    inSetup: null,
    registrationOpen: null,
    closing: null,
    completed: null,
    deleted: null,
  });
  const searchRef = useRef(search);
  searchRef.current = search;
  const parishFilterRef = useRef(parishFilter);
  parishFilterRef.current = parishFilter;
  const projectedParishFilterRef = useRef(projectedParishFilter);
  projectedParishFilterRef.current = projectedParishFilter;
  const projectedTypeFilterRef = useRef(projectedTypeFilter);
  projectedTypeFilterRef.current = projectedTypeFilter;

  const [setupMailOpen, setSetupMailOpen] = useState(false);
  const [setupMailRetreat, setSetupMailRetreat] =
    useState<LifecycleRetreat | null>(null);
  const [setupMailMessage, setSetupMailMessage] = useState("");

  const [selectedRetreats, setSelectedRetreats] = useState<number[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [showFreeze, setShowFreeze] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [editBox, setEditBox] = useState(false);
  const [alertData, setAlertData] = useState<any>({});
  const [contactData, setContactData] = useState(false);
  const [confirmedData, setConfirmedData] = useState(false);
  const [detailRow, setDetailRow] = useState<LifecycleRetreat | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [fileType, setFileType] = useState("");
  const [existingFileUrl, setExistingFileUrl] = useState<string | null>("");
  const [uploadModalTitle, setUploadModalTitle] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<LifecycleRetreat | null>(
    null,
  );

  // Keep actor as a plain function (not useCallback on userData) so loadSection
  // stays referentially stable — otherwise the mount load effect re-fires forever.
  const actor = () => ({
    actorName: userData?.userName || "Admin",
    actorRole: userData?.userRole || "ADMIN",
    actorUserId: userData?.id || null,
    isAdmin: isSuperAdmin,
    isSuperAdmin,
    accessId: userData?.id || null,
  });

  const loadSection = useCallback(
    async (
      key: SectionKey,
      pageNo = 1,
      size = 10,
      sortBy: any = {},
      extra: Record<string, unknown> = {},
    ) => {
      if (key === "projected" && !isSuperAdmin) {
        setLoadingSection((s) => ({ ...s, projected: false }));
        return;
      }
      setLoadingSection((s) => ({ ...s, [key]: true }));
      try {
        const sortColumn = sortBy?.sortColumn || "updatedAt";
        const sortDirection = sortBy?.sortDirection === 1 ? "ASC" : "DESC";
        const res = await retreatApi.listByStatus({
          section: key,
          searchKeyword: extra.searchKeyword ?? searchRef.current[key],
          pageNo,
          size,
          sortColumn,
          sortDirection,
          parishValue:
            key === "registrationOpen" || key === "projected"
              ? extra.parishValue !== undefined
                ? extra.parishValue
                : (key === "projected"
                    ? projectedParishFilterRef.current
                    : parishFilterRef.current) || null
              : null,
          typeFilter:
            key === "projected"
              ? extra.typeFilter !== undefined
                ? extra.typeFilter
                : projectedTypeFilterRef.current || null
              : null,
          ...actor(),
        });
        if (res?.success) {
          setRows((r) => ({ ...r, [key]: res.data || [] }));
          setTotals((t) => ({ ...t, [key]: res.total || 0 }));
        }
      } catch (e: any) {
        toast.error(e?.toString?.() || `Failed to load ${key}`);
      } finally {
        setLoadingSection((s) => ({ ...s, [key]: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- actor reads latest userData; do not depend on it
    [isSuperAdmin, retreatApi],
  );

  const loadDashboard = useCallback(async () => {
    try {
      const res = await retreatApi.getManageDashboard(actor());
      if (res?.success) {
        setCounts({
          projected: res.data.counts.projected || 0,
          inSetup: res.data.counts.inSetup || 0,
          registrationOpen: res.data.counts.registrationOpen || 0,
          closing: res.data.counts.closing || 0,
          completed: res.data.counts.completed || 0,
          deleted: res.data.counts.deleted || 0,
        });
      }
    } catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retreatApi, isSuperAdmin]);

  useEffect(() => {
    authApi.getPerishList().then((res: any) => {
      if (res?.data) {
        setParishes(sortParishesByName(res.data));
      }
    });
    if (
      !isSuperAdmin &&
      userData &&
      (userData.access === true || userData.committeeMember === true)
    ) {
      retreatApi
        .getAllAccessedRetreats({ userId: userData.id })
        .then((res: any) => {
          if (res?.success && Array.isArray(res.data)) {
            setAccessedRetreats(res.data);
          }
        })
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadDashboard();
      if (cancelled) return;
      (Object.keys(SECTION_IDS) as SectionKey[]).forEach((k) => {
        if (k === "projected" && !isSuperAdmin) return;
        loadSection(k, 1, k === "closing" ? 50 : 10);
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [loadDashboard, loadSection, isSuperAdmin]);

  const onSearchChange = (key: SectionKey, value: string) => {
    setSearch((s) => ({ ...s, [key]: value }));
    if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]!);
    debounceRef.current[key] = setTimeout(() => {
      loadSection(
        key,
        1,
        key === "closing" ? 50 : 10,
        {},
        { searchKeyword: value },
      );
    }, 400);
  };

  const scrollTo = (key: SectionKey) => {
    document.getElementById(SECTION_IDS[key])?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    const state = location.state as {
      section?: SectionKey;
      searchKeyword?: string;
    } | null;
    if (!state?.section) return;
    if (state.searchKeyword) {
      setSearch((s) => ({ ...s, [state.section!]: state.searchKeyword! }));
      loadSection(
        state.section,
        1,
        state.section === "closing" ? 50 : 10,
        {},
        { searchKeyword: state.searchKeyword },
      );
    }
    const timer = window.setTimeout(() => scrollTo(state.section!), 500);
    return () => window.clearTimeout(timer);
  }, [loadSection, location.state]);

  const reloadAll = async () => {
    await loadDashboard();
    await Promise.all(
      (Object.keys(SECTION_IDS) as SectionKey[])
        .filter((k) => k !== "projected" || isSuperAdmin)
        .map((k) => loadSection(k)),
    );
  };

  const askDelete = (r: LifecycleRetreat) => {
    if (!isSuperAdmin) return;
    setDeleteTarget(r);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const res = await retreatApi.softDeleteRetreat({
      id: deleteTarget.id,
      ...actor(),
    });
    if (res?.success) {
      toast.success(res.msg);
      setDeleteTarget(null);
      await reloadAll();
    } else toast.error(res?.msg || "Delete failed");
  };

  const restore = async (r: LifecycleRetreat) => {
    const res = await retreatApi.restoreDeletedRetreat({
      id: r.id,
      ...actor(),
    });
    if (res?.success) {
      toast.success(res.msg);
      setRestoreTarget(null);
      await reloadAll();
    } else toast.error(res?.msg || "Restore failed");
  };

  const approveFromTable = async (r: LifecycleRetreat) => {
    const res = await retreatApi.approveAndPublishRetreat({
      id: r.id,
      ...actor(),
    });
    if (res?.success) {
      toast.success(res.msg || "Published.");
      await reloadAll();
    } else toast.error(res?.msg || "Publish failed");
  };

  const buildSetupMailMessage = (row: LifecycleRetreat) => {
    const loc = row.location
      ? `${row.location.charAt(0).toUpperCase()}${row.location.slice(1)}`
      : null;
    const retreatName = [
      row.parishName || row.title,
      row.language ? `(${formatLanguage(row.language)})` : null,
      loc,
    ]
      .filter(Boolean)
      .join(" ");
    return `<p>Hello,</p>
<p>Regarding retreat setup for <b>${retreatName || "this retreat"}</b>.</p>
<p><b>Publication readiness:</b> ${row.readinessPercent || 0}%</p>
<p></p>`;
  };

  const openSetupMail = (row: LifecycleRetreat) => {
    setSetupMailRetreat(row);
    setSetupMailMessage(buildSetupMailMessage(row));
    setSetupMailOpen(true);
  };

  const fetchSetupMailRecipients = async (retreatId: number) => {
    const res = await retreatApi.getSetupMailRecipients({
      retreatId,
      ...actor(),
    });
    if (!res?.success) throw new Error(res?.msg || "Failed to load recipients");
    return res.data;
  };

  const sendSetupMail = async (
    selectedIds: any[],
    content: string,
    additionalEmails: any[],
  ) => {
    if (!setupMailRetreat?.id) return;
    const res = await retreatApi.sendSetupMail({
      retreatId: setupMailRetreat.id,
      message: content,
      recipientIds: selectedIds,
      additionalEmails,
      ...actor(),
    });
    if (res?.success) {
      toast.success(res.msg || "Emails sent successfully.");
      setSetupMailOpen(false);
      setSetupMailRetreat(null);
    } else {
      toast.error(res?.msg || "Failed to send email");
    }
  };

  const handleShowFreeze = (row: LifecycleRetreat) => {
    setShowFreeze(true);
    setAlertData({
      value: row.freeze,
      retreatId: row.id,
      title: row.title,
      language: row.language,
      location: row.location,
      parishName: row.parishName,
    });
  };

  const retreatFreeze = () => {
    retreatApi
      .retreatFreeze({
        retreatId: alertData.retreatId,
        value: alertData.value,
      })
      .then((res: any) => {
        setShowFreeze(false);
        setAlertData({});
        loadSection("registrationOpen");
        toast.success(res.msg);
      })
      .catch((error: any) => {
        toast.error(error?.toString?.() || "Freeze failed");
      });
  };

  const handleShowStatus = (row: LifecycleRetreat) => {
    setShowStatus(true);
    setAlertData(row);
  };

  const handleEditInactiveReason = (row: LifecycleRetreat) => {
    setEditBox(true);
    handleShowStatus(row);
  };

  const handleCloseStatus = () => {
    setShowStatus(false);
    setAlertData({});
    setEditBox(false);
  };

  const retreatChangeStatus = (reason: any = "") => {
    retreatApi
      .retreatChangeStatus({
        alertData: { ...alertData, inactiveReason: reason },
        userData,
        editBox,
      })
      .then((res: any) => {
        handleCloseStatus();
        loadSection("registrationOpen");
        toast.success(res.message || res.msg);
      })
      .catch((error: any) => {
        toast.error(error?.toString?.() || "Status change failed");
      });
  };

  const exportWaitlist = async (row: LifecycleRetreat) => {
    try {
      const response = await waitListApi.waitListCouplesData({
        retreatId: row.id,
      });
      if (!response?.success || !response.data) {
        toast.error(response?.msg || "Failed to fetch waitlist data");
        return;
      }
      if (!response.data.length) {
        toast.info("Data is empty");
        return;
      }
      const formattedData = response.data.map((el: any) => ({
        "His First Name": el.hisFirstName || "",
        "His Last Name": el.hisLastName || "",
        "His Email": el.hisEmail || "",
        "His Mobile": el.hisMobile || "",
        "Her First Name": el.herFirstName || "",
        "Her Last Name": el.herLastName || "",
        "Her Email": el.herEmail || "",
        "Her Mobile": el.herMobile || "",
        Address: el.address || "",
        City: el.city || "",
        State: el.state || "",
        Zip: el.zip || "",
        Parish: el.parish?.parish || "",
        "Anniversary Date": el.anniversary_date
          ? moment(el.anniversary_date).format("MM-DD-YYYY")
          : "",
      }));
      const wb = utils.book_new();
      const ws = utils.json_to_sheet(formattedData);
      utils.book_append_sheet(wb, ws, "WaitList Couples");
      writeFile(wb, `WaitList_Couples_${moment().format("MMM-DD-YYYY")}.xlsx`);
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong while exporting WaitList couples");
    }
  };

  const exportRetreatData = async () => {
    setExporting(true);
    try {
      let res: any;
      const searchKey = searchRef.current.registrationOpen;
      const parishValue = parishFilterRef.current;
      const isAllSelectedNoFilters = allSelected && !parishValue && !searchKey;
      const isAllSelectedWithFilters =
        allSelected && (parishValue || searchKey);
      const isSpecificSelection = !allSelected && selectedRetreats.length > 0;

      if (isAllSelectedNoFilters) {
        res = await retreatApi.getAllRetreatData({});
      } else if (isAllSelectedWithFilters) {
        res = await retreatApi.getAllRetreats({
          pageNo: 1,
          size: 10000,
          searchKeyword: searchKey,
          parishValue: parishValue || null,
          allRetreat: true,
        });
        if (res.success) {
          const retreatIds = res.data.map((retreat: any) => retreat.id);
          res = await retreatApi.getSelectedRetreatData({
            retreats: retreatIds,
          });
        }
      } else if (!allSelected && parishValue && selectedRetreats.length === 0) {
        toast.error("Please select at least one retreat to export");
        setExporting(false);
        return;
      } else if (isSpecificSelection) {
        res = await retreatApi.getSelectedRetreatData({
          retreats: selectedRetreats,
        });
      } else {
        toast.error("Please select at least one retreat to export");
        setExporting(false);
        return;
      }

      if (!res.success) {
        toast.error(res.msg);
        setExporting(false);
        return;
      }

      const allRetreatsData = res.data || [];
      const groupedData = allRetreatsData.reduce(
        (acc: Record<string, any[]>, entry: any) => {
          const id = entry.retreatId;
          if (!acc[id]) acc[id] = [];
          acc[id].push(entry);
          return acc;
        },
        {},
      );

      const workbook = new ExcelJS.Workbook();
      const headers = [
        "Retreat Title",
        "Date From",
        "Date To",
        "Couple Id",
        "Type",
        "Image",
        "His Last Name",
        "His First Name",
        "Her Last Name",
        "Her First Name",
        "His Email",
        "Her Email",
        "His Mobile",
        "Her Mobile",
        "Service Role",
        "Service Title",
        "Talk",
        "Address",
        "City",
        "State",
        "Zip",
        "Parish",
        "Anniversary Date",
        "Dietary Restrictions Or Physical Limitations",
        "Under Age 35",
        "Emergency Name1",
        "Emergency Relationship1",
        "Emergency Phone1",
        "Emergency Name2",
        "Emergency Relationship2",
        "Emergency Phone2",
        "Referral from a friend/family member",
        "Notes",
      ];
      const missingText = "⚠ This is Missing";

      Object.entries(groupedData).forEach(([retreatId, couples], index) => {
        const list = couples as any[];
        let baseTitle = list[0]?.title || `Retreat-${retreatId}`;
        baseTitle = baseTitle.replace(/[\\/*?:[\]]/g, "");
        let sheetTitle = `${index + 1}_` + baseTitle.slice(0, 25);
        if (sheetTitle.length > 31) sheetTitle = sheetTitle.slice(0, 31);
        const worksheet = workbook.addWorksheet(sheetTitle);
        worksheet.addRow(headers);
        list.forEach((item) => {
          const rowData = [
            item.title || missingText,
            moment(item?.dateFrom)
              .tz("America/New_York")
              .format("MMM-DD-YYYY") || missingText,
            moment(item?.dateTo).tz("America/New_York").format("MMM-DD-YYYY") ||
              missingText,
            item.primaryKey || missingText,
            item.attendeeType || missingText,
            item.imageUrl || missingText,
            item.hisLastName || missingText,
            item.hisFirstName || missingText,
            item.herLastName || missingText,
            item.herFirstName || missingText,
            item.hisEmail || missingText,
            item.herEmail || missingText,
            item.hisMobile || missingText,
            item.herMobile || missingText,
            item.role || "⚠ No Role",
            item.service || "⚠ No Service",
            item.talk || "⚠ No Talk",
            item.address || missingText,
            item.city || missingText,
            item.state || missingText,
            item.zip || missingText,
            item.coupleParish || missingText,
            item.anniversary_date
              ? Helpers.convertUtcToTimezone(item.anniversary_date, "M/D/YYYY")
              : missingText,
            item.allergies || missingText,
            item.under_age_35 || missingText,
            item.emergency_name1 || missingText,
            item.emergency_relationship1 || missingText,
            item.emergency_phone1 || missingText,
            item.emergency_name2 || missingText,
            item.emergency_relationship2 || missingText,
            item.emergency_phone2 || missingText,
            item.referral_from_a_friend_or_family_member || missingText,
            item.notes || missingText,
          ];
          const excelRow = worksheet.addRow(rowData);
          for (let i = 1; i <= rowData.length; i++) {
            const cell = excelRow.getCell(i);
            if (cell.value === missingText) {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFF00" },
              };
            }
          }
        });
        worksheet.columns.forEach((column) => {
          let maxLength = 0;
          column.eachCell?.({ includeEmpty: true }, (cell) => {
            const val = cell.value ? cell.value.toString() : "";
            maxLength = Math.max(maxLength, val.length);
          });
          column.width = maxLength + 2;
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Retreats_Export.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch data");
    } finally {
      setExporting(false);
    }
  };

  const openPasswordModal = async () => {
    try {
      const res = await retreatApi.getPassword();
      setPasswordValue(res?.data?.password || res?.password || "");
    } catch {
      setPasswordValue("");
    }
    setShowPasswordMailModal(true);
  };

  const handleUploadProgram = (retreat: LifecycleRetreat) => {
    setSelectedRowId(retreat.id);
    setUploadModalTitle(retreat.title || "");
    setFileType("programFile");
    setShowUploadModal(true);
    setExistingFileUrl(retreat.programFile || "");
  };

  const openHistory = useCallback(
    async (retreat: LifecycleRetreat) => {
      setHistoryRetreat(retreat);
      setHistoryLoading(true);
      setHistoryEntries([]);
      try {
        const res = await retreatApi.getRetreatHistory({
          retreatId: retreat.id,
        });
        if (res?.success) setHistoryEntries(res.data || []);
        else toast.error(res?.msg || "Failed to load retreat history");
      } catch {
        toast.error("Failed to load retreat history");
      } finally {
        setHistoryLoading(false);
      }
    },
    [retreatApi],
  );

  const handleUploadDirectory = (retreat: LifecycleRetreat) => {
    setSelectedRowId(retreat.id);
    setUploadModalTitle(retreat.title || "");
    setFileType("directoryFile");
    setShowUploadModal(true);
    setExistingFileUrl(retreat.directoryFile || "");
  };

  const handleUploadModalClose = () => {
    setShowUploadModal(false);
    setSelectedRowId(null);
    setFileType("");
    setExistingFileUrl("");
    setUploadModalTitle("");
  };

  const openColumns = useMemo(
    () =>
      registrationOpenColumns({
        isSuperAdmin,
        canEdit,
        onStatusToggle: handleShowStatus,
        onEditInactiveReason: handleEditInactiveReason,
        onWaitlistExport: exportWaitlist,
        onDelete: askDelete,
        onFreezeToggle: handleShowFreeze,
        onUploadDirectory: handleUploadDirectory,
        onUploadProgram: handleUploadProgram,
        onCreatedBy: (row) => {
          setDetailRow(row);
          setContactData(true);
        },
        onConfirmedBy: (row) => {
          setDetailRow(row);
          setConfirmedData(true);
        },
        onHistory: openHistory,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSuperAdmin, canEdit, openHistory],
  );

  const fetchFor = useMemo(() => {
    const make =
      (key: SectionKey) =>
      (pageNo: number, size: number, _s: any, sortBy: any) => {
        loadSection(key, pageNo, size, sortBy);
      };
    return {
      projected: make("projected"),
      inSetup: make("inSetup"),
      registrationOpen: make("registrationOpen"),
      closing: make("closing"),
      completed: make("completed"),
      deleted: make("deleted"),
    };
  }, [loadSection]);

  const confirmation = Array.isArray(detailRow?.confirmation)
    ? detailRow?.confirmation
    : [];

  return {
    userData,
    isSuperAdmin,
    canEdit,
    retreatApi,
    counts,
    parishes,
    parishFilter,
    setParishFilter,
    parishFilterRef,
    projectedParishFilter,
    setProjectedParishFilter,
    projectedParishFilterRef,
    projectedTypeFilter,
    setProjectedTypeFilter,
    projectedTypeFilterRef,
    showPasswordMailModal,
    setShowPasswordMailModal,
    passwordValue,
    accessedRetreats,
    restoreTarget,
    setRestoreTarget,
    historyRetreat,
    setHistoryRetreat,
    historyEntries,
    setHistoryEntries,
    historyLoading,
    rows,
    totals,
    search,
    loadingSection,
    setupMailOpen,
    setSetupMailOpen,
    setupMailRetreat,
    setSetupMailRetreat,
    setupMailMessage,
    setSetupMailMessage,
    selectedRetreats,
    setSelectedRetreats,
    allSelected,
    setAllSelected,
    exporting,
    showFreeze,
    setShowFreeze,
    showStatus,
    editBox,
    alertData,
    setAlertData,
    contactData,
    setContactData,
    confirmedData,
    setConfirmedData,
    detailRow,
    showUploadModal,
    selectedRowId,
    fileType,
    existingFileUrl,
    uploadModalTitle,
    deleteTarget,
    setDeleteTarget,
    actor,
    loadSection,
    onSearchChange,
    scrollTo,
    reloadAll,
    askDelete,
    confirmDelete,
    restore,
    approveFromTable,
    openSetupMail,
    fetchSetupMailRecipients,
    sendSetupMail,
    handleShowFreeze,
    retreatFreeze,
    handleShowStatus,
    handleEditInactiveReason,
    handleCloseStatus,
    retreatChangeStatus,
    exportWaitlist,
    exportRetreatData,
    openPasswordModal,
    handleUploadProgram,
    openHistory,
    handleUploadDirectory,
    handleUploadModalClose,
    openColumns,
    fetchFor,
    confirmation,
  };
}

export type ManageRetreatApi = ReturnType<typeof useManageRetreat>;
